import React from 'react';
import { Grid, Stack } from '@mui/material';
import EventNoteIcon from '@mui/icons-material/EventNote';
import { FProcessStageBar, FWorkbenchShell } from '../workbench';
import PlanningCycleCards from './PlanningCycleCards';
import PlanningCycleCreatePanel from './PlanningCycleCreatePanel';
import PlanningCycleDetailPanel from './PlanningCycleDetailPanel';
import PlanningCycleFilters from './PlanningCycleFilters';
import PlanningCycleGrid from './PlanningCycleGrid';
import { PlanningCycleFilters as FilterModel, PlanningCycleRow } from './planningCycle.models';
import {
  activatePlanningCycle,
  advancePlanningCycle,
  closePlanningCycle,
  createPlanningCycle,
  fetchBudgetPeriodOptions,
  fetchPlanningCycleCards,
  fetchPlanningCyclePhaseOptions,
  fetchPlanningCycles,
  fetchPlanningCycleStateOptions,
} from './planningCycle.apis';

type State = {
  loading: boolean;
  error: string;
  success: string;
  cards: any;
  rows: PlanningCycleRow[];
  selected: PlanningCycleRow | null;
  budgetPeriods: any[];
  states: any[];
  phases: any[];
  filters: FilterModel;
};

class PlanningCycleWorkbench extends React.Component<any, State> {
  state: State = {
    loading: false,
    error: '',
    success: '',
    cards: {},
    rows: [],
    selected: null,
    budgetPeriods: [],
    states: [],
    phases: [],
    filters: {
      budgetperiodid: null,
      stateid: null,
      phaseid: null,
      status: 1,
      page: 0,
      pagesize: 50,
    },
  };

  componentDidMount() {
    this.initialize();
  }

  initialize = async () => {
    this.setState({ loading: true, error: '' });

    try {
      const [budgetPeriods, states, phases] = await Promise.all([
        fetchBudgetPeriodOptions(),
        fetchPlanningCycleStateOptions(),
        fetchPlanningCyclePhaseOptions(),
      ]);

      this.setState({ budgetPeriods, states, phases }, this.load);
    } catch (err: any) {
      this.setState({ loading: false, error: err.message || String(err) });
    }
  };

  load = async () => {
    this.setState({ loading: true, error: '' });

    try {
      const [cards, rows] = await Promise.all([
        fetchPlanningCycleCards(),
        fetchPlanningCycles(this.state.filters),
      ]);

      const selected =
        this.state.selected &&
        rows.find((r) => r.rid === this.state.selected?.rid);

      this.setState({
        cards,
        rows,
        selected: selected || null,
      });
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  create = async (payload: any) => {
    this.setState({ loading: true, error: '', success: '' });

    try {
      await createPlanningCycle(payload);
      this.setState({ success: 'Planning Cycle created.' });
      await this.load();
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  activate = async (rid: number) => {
    this.setState({ loading: true, error: '', success: '' });

    try {
      await activatePlanningCycle(rid);
      this.setState({ success: 'Planning Cycle activated.' });
      await this.load();
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  advance = async (rid: number, phasecode: string, notes: string) => {
    this.setState({ loading: true, error: '', success: '' });

    try {
      await advancePlanningCycle(rid, phasecode, notes);
      this.setState({ success: `Planning Cycle advanced to ${phasecode}.` });
      await this.load();
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  close = async (rid: number, notes: string) => {
    this.setState({ loading: true, error: '', success: '' });

    try {
      await closePlanningCycle(rid, notes);
      this.setState({ success: 'Planning Cycle closed.' });
      await this.load();
    } catch (err: any) {
      this.setState({ error: err.message || String(err) });
    } finally {
      this.setState({ loading: false });
    }
  };

  clearFilters = () => {
    this.setState(
      {
        filters: {
          budgetperiodid: null,
          stateid: null,
          phaseid: null,
          status: 1,
          page: 0,
          pagesize: 50,
        },
      },
      this.load,
    );
  };

  render() {
    const selectedPhase = this.state.selected?.phasecode || 'HISTORICAL_DATA';

    return (
      <FWorkbenchShell
        title="Planning Cycle"
        subtitle="Create and manage the budget-domain planning exercise before Executive Review and Determinant Budget."
        icon={<EventNoteIcon />}
        loading={this.state.loading}
        error={this.state.error}
        success={this.state.success}
        onRefresh={this.load}
        summary={<PlanningCycleCards cards={this.state.cards} />}
        filters={
          <PlanningCycleFilters
            value={this.state.filters}
            budgetPeriods={this.state.budgetPeriods}
            states={this.state.states}
            phases={this.state.phases}
            loading={this.state.loading}
            onChange={(filters) => this.setState({ filters })}
            onApply={this.load}
            onClear={this.clearFilters}
          />
        }
        processStage={
          <FProcessStageBar
            stages={[
              {
                code: 'BIE',
                label: 'Business Intelligence',
                status: [
                  'HISTORICAL_DATA',
                  'FORECAST_BASELINE',
                  'INTERACTIVE_SIMULATION',
                  'EXECUTIVE_REVIEW',
                ].includes(selectedPhase)
                  ? 'active'
                  : 'done',
              },
              {
                code: 'PLANNING',
                label: 'Planning',
                status: [
                  'DETERMINANT_BUDGET',
                  'ACTIVITY_PLANNING',
                  'ACTIVITY_PROGRAMME',
                  'BUDGET_ALLOCATION',
                  'BUDGET_CONSOLIDATION',
                ].includes(selectedPhase)
                  ? 'active'
                  : 'pending',
              },
              {
                code: 'GOVERNANCE',
                label: 'Governance',
                status: [
                  'BUDGET_VALIDATION',
                  'BUDGET_VERSIONING',
                  'GOVERNANCE',
                ].includes(selectedPhase)
                  ? 'active'
                  : 'pending',
              },
              {
                code: 'DELIVERY',
                label: 'Delivery',
                status: ['PPM', 'EXECUTION', 'MONITORING'].includes(selectedPhase)
                  ? 'active'
                  : 'pending',
              },
            ] as any}
          />
        }
      >
        <Stack spacing={3}>
          <PlanningCycleCreatePanel
            budgetPeriods={this.state.budgetPeriods}
            loading={this.state.loading}
            onCreate={this.create}
          />

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <PlanningCycleGrid
                rows={this.state.rows}
                loading={this.state.loading}
                onSelect={(selected) => this.setState({ selected })}
              />
            </Grid>

            <Grid size={{ xs: 12, lg: 4 }}>
              <PlanningCycleDetailPanel
                cycle={this.state.selected}
                phases={this.state.phases}
                loading={this.state.loading}
                onActivate={this.activate}
                onAdvance={this.advance}
                onClose={this.close}
              />
            </Grid>
          </Grid>
        </Stack>
      </FWorkbenchShell>
    );
  }
}

export default PlanningCycleWorkbench;
